/**
 * Minimal confirm-before-write modal used by CategoryEditor's Save flow.
 *
 * Renders a diff list (`key: old → new` per dirty entry) and Cancel / Confirm
 * buttons. When `requireText` is provided, Confirm stays disabled until the user
 * types the EXACT required string — this is the extra friction gate for the
 * shadow/live master switch (`org.execution_mode`), where a one-click confirm
 * would be too easy to fire by accident.
 *
 * Styling is deferred to Task 8; only semantic classNames are emitted here.
 */

import { useState } from 'react';

export interface DiffRow {
  key: string;
  /** The current (server) value for this entry. */
  oldValue: unknown;
  /** The pending value the user is about to write. */
  newValue: unknown;
}

export interface DiffConfirmProps {
  rows: DiffRow[];
  onCancel: () => void;
  onConfirm: () => void;
  /** When set, Confirm is disabled until the user types this exact text. */
  requireText?: string;
  /** Confirm is disabled (e.g. a write is in flight). */
  busy?: boolean;
}

/** Render a value compactly for the diff line: strings as-is, everything else
 *  as JSON. `undefined` becomes the literal `undefined` so a cleared field is
 *  legible rather than blank. */
function fmt(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export default function DiffConfirm({
  rows,
  onCancel,
  onConfirm,
  requireText,
  busy,
}: DiffConfirmProps) {
  const [typed, setTyped] = useState('');
  const textGateOpen = requireText === undefined || typed === requireText;
  const confirmDisabled = busy || !textGateOpen || rows.length === 0;

  return (
    <div className="diff-confirm-backdrop" role="dialog" aria-modal="true" aria-label="Confirm changes">
      <div className="diff-confirm">
        <h2 className="diff-confirm-title">Confirm changes</h2>
        <ul className="diff-list">
          {rows.map((r) => (
            <li key={r.key} className="diff-row" data-key={r.key}>
              <span className="diff-key">{r.key}</span>
              <span className="diff-change">
                <span className="diff-old">{fmt(r.oldValue)}</span>
                {' → '}
                <span className="diff-new">{fmt(r.newValue)}</span>
              </span>
            </li>
          ))}
        </ul>

        {requireText !== undefined && (
          <label className="diff-confirm-gate">
            <span>
              Type <code>{requireText}</code> to confirm
            </span>
            <input
              type="text"
              className="input"
              value={typed}
              aria-label={`Type ${requireText} to confirm`}
              onChange={(e) => setTyped(e.target.value)}
            />
          </label>
        )}

        <div className="diff-confirm-actions">
          <button type="button" className="btn btn-cancel" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-confirm"
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
